import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import type { Notification } from './types'

const LIST_KEY = ['notifications', 'list'] as const
const COUNT_KEY = ['notifications', 'count'] as const

/**
 * Bell-feed list. Polled every 30s while the tab is open so new events show
 * up without a manual refresh; paused when the window isn't focused to
 * avoid wasted requests.
 */
export function useNotifications() {
  const api = useApi()
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const res = await api<{ notifications: Notification[] }>('/api/notifications')
      if (!res.success) throw new Error(res.error ?? 'Failed to load notifications')
      return res.data?.notifications ?? []
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  })
}

/**
 * Small unread-count badge. Refreshes more frequently than the list itself
 * so the bell dot updates even when the drawer isn't open.
 */
export function useUnreadCount() {
  const api = useApi()
  return useQuery({
    queryKey: COUNT_KEY,
    queryFn: async () => {
      const res = await api<{ unread: number }>('/api/notifications/count')
      if (!res.success) throw new Error(res.error ?? 'Failed to load count')
      return res.data?.unread ?? 0
    },
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  })
}

export function useMarkRead() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api(`/api/notifications/read/${id}`, { method: 'POST' })
      if (!res.success) throw new Error(res.error ?? 'Failed to mark read')
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
      qc.invalidateQueries({ queryKey: COUNT_KEY })
    },
  })
}

export function useMarkAllRead() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api('/api/notifications/read-all', { method: 'POST' })
      if (!res.success) throw new Error(res.error ?? 'Failed to mark all read')
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
      qc.invalidateQueries({ queryKey: COUNT_KEY })
    },
  })
}
